"use client";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePassportProvider } from "@/context";
import {
  Action,
  ControlType,
} from "@/context/AnalyticsProvider/AnalyticsProvider";
import {
  GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import { GameOnboardingProvider } from "@/context/GameOnboardingContext";
import { cn } from "@/lib/utils";
import { XIcon } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useState } from "react";
import { GlassButton } from "../GlassButton";
import { AnimatedEffect } from "./AnimatedEffect";
import { BronzeRewardAchieved } from "./BronzeRewardAchieved";
import { FinalReward } from "./FinalReward";
import { FollowOnX } from "./FollowOnX";
import { GPOnboardingLogin } from "./GPOnboardingLogin";
import { LinkSteam } from "./LinkSteam";
import { SilverRewardAchieved } from "./SilverRewardAchieved";
import { WishlistSteam } from "./WishlistSteam";

interface GPOnboardingModalProps {
  open: boolean;
  onClose: () => void;
}

// Main Modal Component
export function GPOnboardingModal({ open, onClose }: GPOnboardingModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const { isLoggedIn, isLoggingIn, walletAddress } = usePassportProvider();
  const { track } = useGamePageAnalytics();
  const { addToWishlist } = useGameDetailsContext();

  useEffect(() => {
    if (walletAddress) {
      addToWishlist();
    }
  }, [walletAddress, addToWishlist]);

  useEffect(() => {
    if (!open) return;

    track({
      action: Action.VIEWED,
      extras: {
        control: `step-${currentStep + 1}`,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });
  }, [track, currentStep, open]);

  const handleNext = useCallback(() => {
    setCurrentStep((prev) => prev + 1);
  }, []);

  const handleClose = useCallback(
    (open: boolean) => {
      // if modal becomes open from closed, ignore handleClose
      if (open) {
        track({
          action: Action.OPENED,
          extras: {
            control: "onboarding-modal",
          },
        });
        return;
      }

      track({
        action: Action.CLOSED,
        extras: {
          control: "onboarding-modal",
        },
      });

      setCurrentStep(0);
      onClose();
    },
    [onClose, track],
  );

  const GPOnboardingScreens = useMemo(
    () => [
      <GPOnboardingLogin
        key={1}
        onNext={handleNext}
        isVisible={currentStep === 0}
        track={track}
      />,
      <BronzeRewardAchieved
        key={2}
        onNext={handleNext}
        isVisible={currentStep === 1}
        track={track}
      />,
      <LinkSteam
        key={3}
        onNext={handleNext}
        isVisible={currentStep === 2}
        track={track}
      />,
      <SilverRewardAchieved
        key={4}
        onNext={handleNext}
        isVisible={currentStep === 3}
        track={track}
      />,
      <WishlistSteam
        key={5}
        onNext={handleNext}
        isVisible={currentStep === 4}
        track={track}
      />,
      <FollowOnX
        key={6}
        onNext={handleNext}
        isVisible={currentStep === 5}
        track={track}
      />,
      <FinalReward
        key={7}
        onNext={onClose}
        isVisible={currentStep === 6}
        track={track}
      />,
    ],
    [handleNext, currentStep, onClose, track],
  );

  const handlePrizeImgClick = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "prize",
        controlType: ControlType.IMAGE,
        section: GamePageSection.ONBOARDING_MODAL,
        controlLabel:
          currentStep >= 6
            ? "Gold Prize"
            : currentStep >= 4
              ? "Silver Prize"
              : currentStep >= 2
                ? "Bronze Prize"
                : "Default Prize",
      },
    });
  }, [track, currentStep]);

  const handleLogoClick = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "logo",
        controlType: ControlType.IMAGE,
        section: GamePageSection.ONBOARDING_MODAL,
        controlLabel: "8 Ball Logo",
      },
    });
  }, [track]);

  const handleProgressBarClick = useCallback(
    (label: string) => {
      track({
        action: Action.PRESSED,
        extras: {
          control: "progress-bar",
          controlLabel: label,
          section: GamePageSection.ONBOARDING_MODAL,
        },
      });
    },
    [track],
  );

  // Lock body scroll when modal is open since we are using modal={false}
  useEffect(() => {
    if (open) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [open]);

  return (
    <GameOnboardingProvider>
      {/* Custom Overlay to prevent flickering when switching modal modes */}
      <div
        className={cn(
          "fixed inset-0 bg-black/40 transition-opacity duration-200",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
          isLoggingIn ? "z-[0] pointer-events-none" : "z-[99]",
        )}
        onClick={() => !isLoggingIn && handleClose(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !isLoggingIn) handleClose(false);
        }}
        role="button"
        tabIndex={-1}
        aria-label="Close modal overlay"
      />
      {/* modal={false} prevents the focus trap and allows outside interactions (Passport iframe) */}
      <Dialog open={open} onOpenChange={handleClose} modal={false}>
        <DialogContent
          className={cn(
            "font-suisse flex gap-y-0 flex-col !rounded-[2.5rem] bg-neutral-900/30 backdrop-blur-[32px] h-[95vh] w-[95vw] max-h-[50rem] max-w-xl glass-container-border before:!opacity-100 before:!rounded-[calc(2.5rem+1px)] after:!opacity-0 border-0 px-[clamp(0.5rem,12.5vw,5rem)] py-[clamp(1rem,6.25vh,3rem)]",
            isLoggingIn ? "!z-[1]" : "!z-[100]",
          )}
          style={isLoggingIn ? { pointerEvents: "none" } : undefined}
          removeCloseButton
          // Hide the native overlay since we are using a custom one
          overlayClassName="hidden"
        >
          <DialogTitle className="sr-only">
            Custom Steam Onboarding Modal
          </DialogTitle>

          <div className="relative mx-auto h-fit w-full">
            <AnimatedEffect className="absolute w-full h-full" tint="#D37A38" />
            <div className="relative w-[258px] h-[242px] mx-auto">
              <Image
                src={
                  currentStep >= 6
                    ? "/assets/steam-onboarding/prize-8ball-gold.webp"
                    : currentStep >= 4
                      ? "/assets/steam-onboarding/prize-8ball-silver.webp"
                      : currentStep >= 2
                        ? "/assets/steam-onboarding/prize-8ball-bronze.webp"
                        : "/assets/steam-onboarding/prize-8ball-default.webp"
                }
                alt="8 Ball loot crate"
                width={258}
                height={242}
                className="drop-shadow-2xl object-contain w-full h-full"
                onClick={handlePrizeImgClick}
              />
            </div>

            {!isLoggedIn ? (
              <Image
                width={330}
                height={111}
                src="/assets/steam-onboarding/logo.webp"
                alt="Immutable 8 Ball Rewards"
                className="-bottom-6 left-1/2 -translate-x-1/2 absolute w-3/5"
                onClick={handleLogoClick}
              />
            ) : null}
          </div>
          <div className="p-2 gap-x-0.5 flex items-center">
            {/* First Progress Bar - Bronze */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: tracking only onclick */}
            <div
              className={cn(
                "min-w-0 flex-1 relative h-2 rounded-[0.125rem] glass-container-border before:rounded-[0.125rem] before:!inset-0 before:!opacity-100 after:!opacity-0 transition-all duration-500",
                currentStep >= 2
                  ? "bg-gradient-to-r from-[#D37A38] to-[#F5A623]"
                  : "bg-white/20",
                currentStep === 1 && "animate-pulse",
              )}
              onClick={() => handleProgressBarClick("Segment 1")}
            />
            {/* Bronze Crate */}
            <div className="relative max-w-11 w-[7.5vw]">
              {currentStep === 1 && (
                <div className="absolute inset-0 -m-2 animate-pulse">
                  <AnimatedEffect
                    className="absolute w-full h-full"
                    tint="#D37A38"
                  />
                </div>
              )}
              <Image
                src="/assets/steam-onboarding/prize-8ball-bronze.webp"
                alt=""
                width={44}
                height={44}
                className={cn(
                  "max-w-11 w-[7.5vw] relative z-10 transition-transform duration-300",
                  currentStep === 1 && "scale-110",
                )}
                onClick={() => handleProgressBarClick("Bronze Crate")}
              />
            </div>
            {/* Second Progress Bar - Silver */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: tracking only onclick */}
            <div
              className={cn(
                "min-w-0 flex-1 relative h-2 rounded-[0.125rem] glass-container-border before:rounded-[0.125rem] before:!inset-0 before:!opacity-100 after:!opacity-0 transition-all duration-500",
                currentStep >= 4
                  ? "bg-gradient-to-r from-[#D37A38] to-[#F5A623]"
                  : "bg-white/20",
                currentStep === 3 && "animate-pulse",
              )}
              onClick={() => handleProgressBarClick("Segment 2")}
            />
            {/* Silver Crate */}
            <div className="relative max-w-11 w-[7.5vw]">
              {currentStep === 3 && (
                <div className="absolute inset-0 -m-2 animate-pulse">
                  <AnimatedEffect
                    className="absolute w-full h-full"
                    tint="#D37A38"
                  />
                </div>
              )}
              <Image
                src="/assets/steam-onboarding/prize-8ball-silver.webp"
                alt=""
                width={44}
                height={44}
                className={cn(
                  "max-w-11 w-[7.5vw] relative z-10 transition-transform duration-300",
                  currentStep === 3 && "scale-110",
                )}
                onClick={() => handleProgressBarClick("Silver Crate")}
              />
            </div>
            {/* Third Progress Bar - Gold */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: tracking only onclick */}
            <div
              className={cn(
                "min-w-0 flex-1 relative h-2 rounded-[0.125rem] glass-container-border before:rounded-[0.125rem] before:!inset-0 before:!opacity-100 after:!opacity-0 transition-all duration-500",
                currentStep >= 6
                  ? "bg-gradient-to-r from-[#D37A38] to-[#F5A623]"
                  : "bg-white/20",
                currentStep === 5 && "animate-pulse",
              )}
              onClick={() => handleProgressBarClick("Segment 3")}
            />
            {/* Gold Crate */}
            <div className="relative max-w-11 w-[7.5vw]">
              {currentStep === 5 && (
                <div className="absolute inset-0 -m-2 animate-pulse">
                  <AnimatedEffect
                    className="absolute w-full h-full"
                    tint="#D37A38"
                  />
                </div>
              )}
              <Image
                src="/assets/steam-onboarding/prize-8ball-gold.webp"
                alt=""
                width={44}
                height={44}
                className={cn(
                  "max-w-11 w-[7.5vw] relative z-10 transition-transform duration-300",
                  currentStep === 5 && "scale-110",
                )}
                onClick={() => handleProgressBarClick("Gold Crate")}
              />
            </div>
          </div>

          {/* Onboarding Steps */}
          <div className="relative overflow-hidden flex-grow">
            <div
              className="flex h-full transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentStep * 100}%)`,
              }}
            >
              {GPOnboardingScreens.map((Step: JSX.Element, index: number) => (
                <div
                  key={Step.key}
                  className={cn(
                    "w-full flex-shrink-0 transition-opacity duration-500 ease-in-out flex text-center",
                    index === currentStep ? "opacity-100" : "opacity-0",
                  )}
                  style={{
                    contentVisibility: "auto",
                  }}
                >
                  {Step}
                </div>
              ))}
            </div>
          </div>

          <DialogClose
            className="absolute right-4 top-4"
            onClick={() => handleClose(false)}
            asChild
          >
            <GlassButton className="size-10 bg-white/10">
              <XIcon className="size-4" />
            </GlassButton>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </GameOnboardingProvider>
  );
}
